import type {
  FormState,
  ResolvedDataSource,
} from '../../providers/DataSourceManager.types';

/**
 * Minimal context needed to resolve a binding/predicate `source` name to
 * its runtime root object. Structurally compatible with the wider
 * `BindingContext` in `resolveBindings.ts` and the visibility predicate
 * context in `binding-enhancer/visibility.ts`, so both surfaces share one
 * definition of what `item` / `formState` / `context` / `<dataSource>`
 * resolve to.
 */
export interface RootSourceContext {
  readonly sources: Readonly<Record<string, ResolvedDataSource>>;
  readonly item?: unknown;
  readonly formState: FormState;
  readonly flowContext: Readonly<Record<string, unknown>>;
}

/**
 * Resolve the root object used for `getByPath()` for a given source name:
 *   - `'item'`      → the repeater-scope item (may be `undefined`
 *                     outside a repeater; `getByPath` handles the rest).
 *   - `'formState'` → DSM-managed form state. Mirrors the
 *                     `{{formState.<key>}}` interpolation root so the
 *                     structured `_bind` form, the inline-template form,
 *                     and the visibility predicate address the same data.
 *   - `'context'`   → host-supplied `flowContext`. Mirrors
 *                     `{{context.<key>}}`.
 *   - anything else → the source's `.data` payload, or `undefined`
 *                     when the source is still pending / errored /
 *                     absent.
 *
 * Reserved roots (`item` / `formState` / `context`) are NOT registered as
 * data-source dependencies by callers — see `resolveBindProp`.
 */
export function rootForSource(source: string, ctx: RootSourceContext): unknown {
  if (source === 'item') {
    return ctx.item;
  }
  if (source === 'formState') {
    return ctx.formState;
  }
  if (source === 'context') {
    return ctx.flowContext;
  }
  const resolved = ctx.sources[source];
  return resolved?.data;
}
